import React, { useEffect } from 'react';
import styled from 'styled-components';
import { keyframes } from 'styled-components';
import { Flex } from '../../../styles/components';
import { Card, CardBody, CardBadge } from '../../../styles/components/ModernCard';
import { useProStudentNotifications, useStudentNotifications } from '../../../utils/notificationUtils';

// Glow animation for card border
const borderPulse = keyframes`
  0% { box-shadow: 0 0 0 rgba(99, 102, 241, 0), 0 0 8px rgba(99, 102, 241, 0.3); }
  50% { box-shadow: 0 0 12px rgba(99, 102, 241, 0.4), 0 0 18px rgba(99, 102, 241, 0.2); }
  100% { box-shadow: 0 0 0 rgba(99, 102, 241, 0), 0 0 8px rgba(99, 102, 241, 0.3); }
`;

const CycleCard = styled(Card)`
  margin-bottom: 2.5rem;
  position: relative;
  overflow: hidden;
  background: rgba(15, 23, 42, 0.65);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-left: 4px solid ${props => props.theme.colors.primary};
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  
  /* Enhanced glow effect */
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(99, 102, 241, 0.1);
  animation: ${borderPulse} 4s infinite ease-in-out;
  
  /* Space background gradients */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 15% 50%, rgba(99, 102, 241, 0.12) 0%, transparent 45%),
      radial-gradient(circle at 85% 30%, rgba(124, 58, 237, 0.08) 0%, transparent 45%);
    z-index: -1;
  }
  
  /* Star-like dots in background */
  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background-image: 
      radial-gradient(1px 1px at 20% 30%, rgba(255, 255, 255, 0.8) 1px, transparent 0),
      radial-gradient(1px 1px at 40% 70%, rgba(255, 255, 255, 0.7) 1px, transparent 0),
      radial-gradient(1px 1px at 60% 40%, rgba(255, 255, 255, 0.8) 1px, transparent 0),
      radial-gradient(1px 1px at 80% 60%, rgba(255, 255, 255, 0.7) 1px, transparent 0);
    z-index: -1;
    opacity: 0.4;
  }
  
  /* Text color adjustments */
  h3, p, strong {
    color: rgba(255, 255, 255, 0.9);
    text-shadow: 0 0 5px rgba(99, 102, 241, 0.3);
  }
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.75rem;
  background-color: ${props => props.primary ? 'rgba(79, 70, 229, 0.75)' : 'rgba(15, 23, 42, 0.4)'};
  color: ${props => props.primary ? 'white' : 'rgba(255, 255, 255, 0.8)'};
  border: ${props => props.primary ? '1px solid rgba(99, 102, 241, 0.5)' : '1.5px solid rgba(99, 102, 241, 0.6)'};
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  overflow: hidden;
  letter-spacing: 0.5px;
  backdrop-filter: blur(10px);
  text-shadow: ${props => props.primary ? '0 0 8px rgba(255, 255, 255, 0.4)' : 'none'};
  
  /* Enhanced glow effect for primary button */
  ${props => props.primary && `
    box-shadow: 0 0 15px rgba(99, 102, 241, 0.4);
  `}
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      ${props => props.primary 
        ? 'rgba(129, 140, 248, 0.3) 0%, rgba(99, 102, 241, 0.3) 50%, rgba(79, 70, 229, 0.2) 100%'
        : 'rgba(255, 255, 255, 0.1) 0%, rgba(99, 102, 241, 0.1) 50%, rgba(15, 23, 42, 0.1) 100%'}
    );
    z-index: -1;
  }
  
  &:after {
    content: '';
    position: absolute;
    width: 150%;
    height: 200%;
    top: -200%;
    left: -50%;
    background: linear-gradient(
      45deg,
      transparent 0%,
      rgba(255, 255, 255, 0.1) 45%,
      rgba(255, 255, 255, 0.3) 50%,
      rgba(255, 255, 255, 0.1) 55%,
      transparent 100%
    );
    transform: rotate(25deg);
    transition: all 0.8s ease;
    z-index: 1;
  }
  
  &:hover {
    background-color: ${props => props.primary ? 'rgba(79, 70, 229, 0.95)' : 'rgba(99, 102, 241, 0.25)'};
    transform: translateY(-3px);
    box-shadow: ${props => props.primary 
      ? '0 8px 25px -5px rgba(79, 70, 229, 0.5), 0 0 12px rgba(99, 102, 241, 0.5)'
      : '0 7px 20px -5px rgba(31, 38, 135, 0.3), 0 0 10px rgba(99, 102, 241, 0.3)'};
    color: white;
    text-shadow: 0 0 8px rgba(255, 255, 255, 0.6);
    
    &:after {
      top: 200%;
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  svg {
    font-size: 1rem;
    filter: ${props => props.primary ? 'drop-shadow(0 0 2px rgba(255, 255, 255, 0.5))' : 'none'};
  }
`;

const CycleManagement = ({ startDate, endDate, formatDate, onOpenSettings }) => {
  const proStudentNotifier = useProStudentNotifications();
  const studentNotifier = useStudentNotifications();
  
  // Check if cycle is about to begin or has just begun and send notifications
  useEffect(() => {
    const currentDate = new Date();
    const cycleStart = new Date(startDate);
    const daysUntilCycleStart = Math.ceil((cycleStart - currentDate) / (1000 * 60 * 60 * 24));
    
    // If cycle is within 7 days of starting, send upcoming cycle notification
    if (daysUntilCycleStart > 0 && daysUntilCycleStart <= 7) {
      const cycleInfo = {
        id: 'current-cycle',
        name: 'Current Internship',
        startDate: startDate,
        endDate: endDate
      };
      
      // Notify both regular students and pro students
      proStudentNotifier.notifyInternshipCycle(cycleInfo, true);
      studentNotifier.notifyInternshipCycle(cycleInfo, true);
    }
    
    // If cycle just started (within last 2 days), send notification
    if (daysUntilCycleStart >= -2 && daysUntilCycleStart <= 0) {
      const cycleInfo = {
        id: 'current-cycle',
        name: 'Current Internship',
        startDate: startDate,
        endDate: endDate
      };
      
      // Notify both regular students and pro students
      proStudentNotifier.notifyInternshipCycle(cycleInfo, false);
      studentNotifier.notifyInternshipCycle(cycleInfo, false);
    }
  }, [startDate, endDate, proStudentNotifier, studentNotifier]);

  return (
    <CycleCard>
      <CardBody>
        <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap">
          <div>
            <h3 style={{ margin: '0 0 0.5rem 0', color: 'rgba(255, 255, 255, 0.95)' }}>Current Internship Cycle</h3>
            <p style={{ margin: 0, color: 'rgba(255, 255, 255, 0.8)' }}>
              <strong style={{ color: 'rgba(255, 255, 255, 0.95)' }}>{formatDate(startDate)} to {formatDate(endDate)}</strong>
              <CardBadge success style={{ marginLeft: '1rem' }}>Active</CardBadge>
            </p>
          </div>
          <ActionButton 
            primary
            onClick={onOpenSettings}
          >
            Manage Cycle
          </ActionButton>
        </Flex>
      </CardBody>
    </CycleCard>
  );
};

export default CycleManagement;
